import type { ComponentChildren, JSX } from 'preact';
import { useRef } from 'preact/hooks';
import { useSize } from '#hooks/PRIVATE_useSize';
import type { UnpackSignals } from '@oracle/oraclejet-internal-utilities/attributeUtils';
import { Size, sizeToCSS } from '#utils/UNSAFE_size';

type Props = {
  children: (width: number, height: number) => ComponentChildren;
  width?: Size | 'initial';
  height?: Size | 'initial';
} & Pick<UnpackSignals<JSX.IntrinsicElements['div']>, 'class'>;

type TrackResizeContainerOnProps = Omit<Props, 'width' | 'height'> & {
  width?: string;
  height?: string;
};

const TrackResizeContainer = ({ children, width: w, height: h, class: className }: Props) => {
  const width = dimToCSS(w);
  const height = dimToCSS(h);
  const content =
    width && height && isFixedLength(width) && isFixedLength(height) ? (
      <div style={{ width, height }} class={className}>
        {children(fixedLengthToNumber(width), fixedLengthToNumber(height))}
      </div>
    ) : (
      <TrackResizeOnContainer width={width} height={height} class={className}>
        {children}
      </TrackResizeOnContainer>
    );
  return content;
};

const TrackResizeOnContainer = ({
  children,
  width,
  height,
  class: className
}: TrackResizeContainerOnProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const optionsRef = useRef({ box: 'border-box' as const });
  const size = useSize(ref, optionsRef.current);
  return (
    <div ref={ref} style={{ width, height }} class={className}>
      {size && children(size.width, size.height)}
    </div>
  );
};

const isFixedLength = (length: string) => length.endsWith('px') || length === '0';
const fixedLengthToNumber = (length: string) =>
  length === '0' ? 0 : Number(length.substring(0, length.length - 2));

const dimToCSS = (dim: Props['width']) =>
  dim === 'initial' || dim === undefined ? dim : sizeToCSS(dim) || '0px';
export { TrackResizeContainer };
